import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import EmptyMessage from 'core/components/EmptyMessage';
import useAvailableGroupedDocuments from 'hooks/swr/useAvailableGroupedDocuments';
import { isNil } from 'lodash';
import type { Document } from 'models/document/document';
import type { FC } from 'react';
import { useId, useState } from 'react';
import OrderDocumentCard from './OrderDocumentCard';
import OrderDocumentsSkeleton from './OrderDocumentsSkeleton';
import type { SelectChangeEvent } from '@mui/material/Select';

const RenderDocuments: FC<{ documents: Document[]; orderId: number }> = ({
  documents,
  orderId,
}) => (
  <Stack
    gap={2}
    display={'grid'}
    gridTemplateColumns={'repeat(auto-fill, minmax(160px, 1fr))'}>
    {documents.map((doc) => (
      <OrderDocumentCard key={doc.id} document={doc} orderId={orderId} />
    ))}
  </Stack>
);

const OrderDocuments: FC<OrderDocumentsProps> = ({ orderId }) => {
  const componentId = useId();
  const { data: groupedDocuments, isLoading } = useAvailableGroupedDocuments(
    (orderId ?? 0) > 0
  );
  const [selectedGroupId, setSelectedGroupId] = useState(-1);

  if (isLoading || isNil(groupedDocuments)) {
    return <OrderDocumentsSkeleton />;
  }

  if (!groupedDocuments.length) {
    return (
      <Stack my={5} spacing={1} alignItems={'center'} color={'text.secondary'}>
        <MonetizationOnOutlinedIcon fontSize='large' />
        <Typography variant='h6'>Документы не найдены</Typography>
      </Stack>
    );
  }

  const namedGroups = groupedDocuments.filter((g) => g.groupName);
  const noGroup = groupedDocuments.find((g) => !g.groupName);

  if (namedGroups.length > 0) {
    const currentGroup =
      namedGroups.find((g) => g.id === selectedGroupId) ?? namedGroups[0];
    if (!currentGroup) {
      return (
        <Stack my={5} spacing={1} alignItems={'center'} color={'text.secondary'}>
          <EmptyMessage />
        </Stack>
      );
    }
    return (
      <Stack gap={4}>
        <FormControl size='small' sx={{ maxWidth: 320 }}>
          <InputLabel id={`${componentId}-group-select-label`}>
            Группа документов
          </InputLabel>
          <Select<number>
            labelId={`${componentId}-group-select-label`}
            value={currentGroup.id ?? 0}
            label='Группа документов'
            onChange={(e: SelectChangeEvent<number>) => {
              setSelectedGroupId(Number(e.target.value));
            }}>
            {namedGroups.map((group) => (
              <MenuItem
                key={`${componentId}-group-select-item-${String(group.id ?? 0)}-${
                  group.groupName ?? 'no-name'
                }`}
                value={group.id ?? 0}>
                {group.groupName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <RenderDocuments documents={currentGroup.documents} orderId={orderId ?? 0} />
      </Stack>
    );
  }

  if (noGroup && noGroup.documents.length > 0) {
    return <RenderDocuments documents={noGroup.documents} orderId={orderId ?? 0} />;
  }

  return (
    <Stack my={5} spacing={1} alignItems={'center'} color={'text.secondary'}>
      <MonetizationOnOutlinedIcon fontSize='large' />
      <Typography variant='h6'>Документы не найдены</Typography>
    </Stack>
  );
};

export interface OrderDocumentsProps {
  orderId?: number;
}

export default OrderDocuments;
